import { Static, Type } from '@sinclair/typebox';
import type { RichTextTag } from './website_types/BuilderTypes';

// ── Limits ────────────────────────────────────────────

export const BOOKING_INSTRUCTION_MAX_ATTACHMENTS = 5;
export const BOOKING_INSTRUCTION_MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024; // 10 MB
export const BOOKING_INSTRUCTION_ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
] as const;
// Borne du HTML d'une section (= plafond du binding {{appointment:instructions_html}} des emails).
export const BOOKING_INSTRUCTION_CONTENT_MAX_LENGTH = 20000;

// ── Rich text ─────────────────────────────────────────

/**
 * Balises du contenu d'une section : la barre d'outils de l'éditeur, le filtrage d'écriture
 * (`meta.allowed_tags`) et l'assainissement en lecture lisent cette seule liste.
 */
export const BOOKING_INSTRUCTION_RICH_TEXT_TAGS = [
  'b',
  'i',
  'u',
  'a',
  'ul',
  'ol',
  'h3',
] as const satisfies readonly RichTextTag[];

// ── Attachment ────────────────────────────────────────

export const BookingInstructionAttachmentSchema = Type.Object({
  // Opaque token returned by the upload endpoint. The client uses it to keep
  // referencing the attachment in subsequent update/delete calls without ever
  // exposing the raw S3 key.
  attachment_token: Type.String(),
  file_name: Type.String({ maxLength: 255 }),
  mime_type: Type.String({ maxLength: 100 }),
  size_bytes: Type.Number(),
  // Permanent download URL — backend endpoint that 302s to a freshly-signed
  // S3 URL. Stays valid as long as the attachment exists.
  download_url: Type.String(),
});
export type BookingInstructionAttachmentObject = Static<typeof BookingInstructionAttachmentSchema>;

// ── Public response (returned in the booking confirmation flow) ──

export const BookingInstructionPublicSchema = Type.Object({
  booking_instruction_id: Type.Number(),
  title: Type.Optional(Type.String()),
  content_html: Type.String(),
  attachments: Type.Array(BookingInstructionAttachmentSchema),
  sort_order: Type.Number(),
});
export type BookingInstructionPublicObject = Static<typeof BookingInstructionPublicSchema>;

// ── Admin response (returned by the pro back-office endpoints) ────

export const BookingInstructionAdminSchema = Type.Object({
  booking_instruction_id: Type.Number(),
  title: Type.Optional(Type.String()),
  content_html: Type.String(),
  prestation_ids: Type.Array(Type.Number()),
  pro_place_ids: Type.Array(Type.Number()),
  attachments: Type.Array(BookingInstructionAttachmentSchema),
  sort_order: Type.Number(),
  is_archived: Type.Boolean(),
  created_at: Type.Optional(Type.String({ format: 'date-time' })),
  updated_at: Type.Optional(Type.String({ format: 'date-time' })),
});
export type BookingInstructionAdminObject = Static<typeof BookingInstructionAdminSchema>;

// La règle de périmètre (prestations ET lieux) vit dans ./booking_perimeter : source unique partagée
// avec les champs personnalisés.

// ── CRUD requests ─────────────────────────────────────

export const CreateBookingInstructionRequestSchema = Type.Object({
  title: Type.Optional(
    Type.String({
      maxLength: 255,
      title: 'BOOKING_INSTRUCTION_TITLE',
      placeholder: 'BOOKING_INSTRUCTION_TITLE_PLACEHOLDER',
    }),
  ),
  // `meta.rich_text` : @TypeBoxBody assainit ce HTML à l'écriture (sanitizeRichTextFields) ; le service
  // l'assainit AUSSI à la lecture (lignes déjà stockées). maxLength borne l'entrée de l'assainisseur.
  content_html: Type.String({
    minLength: 1,
    maxLength: BOOKING_INSTRUCTION_CONTENT_MAX_LENGTH,
    title: 'BOOKING_INSTRUCTION_CONTENT',
    meta: { input_type: 'rich_text', allowed_tags: [...BOOKING_INSTRUCTION_RICH_TEXT_TAGS] },
  }),
  prestation_ids: Type.Optional(Type.Array(Type.Number())),
  // Vide = tous les lieux ; sinon la section n'est servie que pour une réservation tenue dans l'un d'eux.
  pro_place_ids: Type.Optional(Type.Array(Type.Integer())),
  // Tokens returned by the upload endpoint; the server resolves them to
  // attachment records.
  attachment_tokens: Type.Optional(Type.Array(Type.String())),
  sort_order: Type.Optional(Type.Number()),
});
export type CreateBookingInstructionRequestObject = Static<typeof CreateBookingInstructionRequestSchema>;

export const UpdateBookingInstructionRequestSchema = Type.Partial(CreateBookingInstructionRequestSchema);
export type UpdateBookingInstructionRequestObject = Static<typeof UpdateBookingInstructionRequestSchema>;

export const ReorderBookingInstructionsRequestSchema = Type.Object({
  ordered_ids: Type.Array(Type.Number(), { minItems: 1 }),
});
export type ReorderBookingInstructionsRequestObject = Static<typeof ReorderBookingInstructionsRequestSchema>;

// ── Attachment upload response ────────────────────────

export const BookingInstructionUploadResponseSchema = BookingInstructionAttachmentSchema;
export type BookingInstructionUploadResponseObject = Static<typeof BookingInstructionUploadResponseSchema>;
